import React from 'react';
import { CtaButton } from '../CtaButton/CtaButton';
import { CTA_PREFIXES, CtaTrackingPrefixContext } from '../../hooks/useClickTracking';
import { CustomIcon } from '../CustomIcon/CustomIcon';
import { CustomIconName } from '../CustomIcon/CustomIcon';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaBehavior } from '../../utils/ctaBehavior';
import { Language } from './LanguagePicker';
import { MobileAccordion } from './MobileAccordion';
import { MegaMenuCta } from './MegaMenuCta';
import { NavFooter } from './NavFooter';
import { NavFooterItem, PrimaryNavGroup } from './MegaMenu';
import type { MegaMenuCtaAction } from './MegaMenu';

export interface MobileDrawerProps {
  /** Unique ID for the mobile view wrapper (used by focus trap in parent) */
  mobileId: string;
  /** Unique ID for the drawer element (generated via useId in parent) */
  drawerId: string;
  /** Unique ID for the language picker drawer (generated via useId in parent) */
  langDrawerId: string;

  /** Pre-built logo element from MegaMenu shell */
  logoSlot?: React.ReactNode;

  /** L1 navigation groups rendered as accordion rows */
  primaryNavGroups: PrimaryNavGroup[];
  /** Currently expanded accordion group ID */
  selectedPrimaryNavGroupId: string;
  /** Toggle expand/collapse of an accordion group by ID */
  toggleSelectedPrimaryNavGroupId: (id: string) => void;

  /** Footer links shown below the accordion, the same items desktop shows under each dropdown */
  navFooterItems?: NavFooterItem[];

  /** Whether the mobile drawer is open */
  isNavMenuExpanded: boolean;
  /** Whether drawer open/close should animate */
  drawerAnimated: boolean;
  /** Toggle drawer open/close */
  toggleNavMenu: () => void;

  /** Show search trigger in mobile nav bar */
  enableSearch: boolean;
  /** Placeholder text for search (also used as aria-label) */
  searchPlaceholder?: string;
  /** Accessible name for the drawer */
  mobileMenuAriaLabel?: string;
  /** Callback when search trigger is clicked */
  handleSearchToggle: () => void;

  /** Show language picker in drawer footer */
  enableLanguagePicker: boolean;
  /** Available languages for the picker */
  parsedLanguages: Language[];
  /** Current selected language code */
  languagePickerCurrentLanguage?: string;
  /** Whether the language picker drawer is open */
  isLanguagePickerExpanded: boolean;
  /** Toggle language picker drawer open/close */
  toggleLanguagePickerDrawer: () => void;

  /** Primary CTA button text */
  primaryCtaText?: string;
  /** Primary CTA destination URL */
  primaryCtaUrl?: string;
  /** Primary CTA tracking ID */
  primaryCtaId?: string;
  /** Primary CTA LD appendix for analytics */
  primaryCtaLdAppendix?: string;
  /** Primary CTA tab behavior */
  primaryCtaBehavior?: CtaBehavior;
  /** Primary CTA action */
  primaryCtaAction?: MegaMenuCtaAction;
  /** Popup target ID when primary CTA Action is Popup */
  primaryCtaTargetPopupId?: string;

  /** Secondary CTA button text */
  secondaryCtaText?: string;
  /** Secondary CTA destination URL */
  secondaryCtaUrl?: string;
  /** Secondary CTA tracking ID */
  secondaryCtaId?: string;
  /** Secondary CTA LD appendix for analytics */
  secondaryCtaLdAppendix?: string;
  /** Secondary CTA tab behavior */
  secondaryCtaBehavior?: CtaBehavior;
  /** Secondary CTA action */
  secondaryCtaAction?: MegaMenuCtaAction;
  /** Popup target ID when secondary CTA Action is Popup */
  secondaryCtaTargetPopupId?: string;
  /** Icon displayed alongside secondary CTA text */
  secondaryCtaIconName?: CustomIconName;
}

/**
 * MobileDrawer — Mobile navigation view for MegaMenu
 *
 * Renders the mobile nav bar (hamburger + logo + search trigger + CTA)
 * and the slide-out drawer (accordion + footer). All state is managed
 * by the parent MegaMenu shell; this component is purely presentational.
 */
export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  mobileId,
  drawerId,
  langDrawerId,
  logoSlot,
  primaryNavGroups,
  selectedPrimaryNavGroupId,
  toggleSelectedPrimaryNavGroupId,
  navFooterItems,
  isNavMenuExpanded,
  drawerAnimated,
  toggleNavMenu,
  enableSearch,
  searchPlaceholder,
  mobileMenuAriaLabel,
  handleSearchToggle,
  enableLanguagePicker,
  parsedLanguages,
  languagePickerCurrentLanguage,
  isLanguagePickerExpanded,
  toggleLanguagePickerDrawer,
  primaryCtaText,
  primaryCtaUrl,
  primaryCtaId,
  primaryCtaLdAppendix,
  primaryCtaBehavior,
  primaryCtaAction,
  primaryCtaTargetPopupId,
  secondaryCtaText,
  secondaryCtaUrl,
  secondaryCtaId,
  secondaryCtaLdAppendix,
  secondaryCtaBehavior,
  secondaryCtaAction,
  secondaryCtaTargetPopupId,
  secondaryCtaIconName,
}) => (
  <div id={mobileId} className="poly-mega-menu__mobile">
    <div className="poly-mega-menu__nav-container">
      {/* Left side: hamburger + logo */}
      <div className="poly-mega-menu__mobile-left">
        <button
          type="button"
          className="poly-mega-menu__hamburger"
          aria-label={isNavMenuExpanded ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={isNavMenuExpanded}
          aria-controls={drawerId}
          onClick={toggleNavMenu}
        >
          <CustomIcon
            name={isNavMenuExpanded ? 'close' : 'menu'}
            className="poly-mega-menu__hamburger-icon"
            aria-hidden="true"
          />
        </button>
        {logoSlot}
      </div>

      {/* Right side: search icon (only when expanded) + primary CTA (only when collapsed) */}
      <div className="poly-mega-menu__mobile-right">
        {enableSearch && (
          <button
            type="button"
            className="poly-mega-menu__mobile-search-trigger"
            aria-label={searchPlaceholder || 'Search'}
            onClick={handleSearchToggle}
          >
            <CustomIcon name="search" className="poly-mega-menu__mobile-search-icon" aria-hidden="true" />
          </button>
        )}
        {primaryCtaText && hasCtaDestination(primaryCtaUrl, primaryCtaAction, primaryCtaTargetPopupId) && (
          <div className="poly-mega-menu__mobile-cta">
            <CtaTrackingPrefixContext.Provider value={CTA_PREFIXES.nav}>
              <CtaButton
                text={primaryCtaText}
                href={primaryCtaUrl}
                action={primaryCtaAction}
                popupTargetId={primaryCtaTargetPopupId}
                behavior={primaryCtaBehavior}
                data-cta-id={primaryCtaId}
                ldAppendix={primaryCtaLdAppendix}
                size="nav"
              />
            </CtaTrackingPrefixContext.Provider>
          </div>
        )}
      </div>
    </div>

    {/* Mobile Drawer — slides from left, full viewport height */}
    <section
      className={`poly-mega-menu__drawer${isNavMenuExpanded ? ' poly-mega-menu__drawer--open' : ''}${drawerAnimated ? ' poly-mega-menu__drawer--animated' : ''}`}
      id={drawerId}
      aria-label={mobileMenuAriaLabel || 'Navigation menu'}
    >
      <div className="poly-mega-menu__drawer-scroll">
        {/* Accordion — L1 nav groups as expandable rows */}
        <MobileAccordion
          primaryNavGroups={primaryNavGroups}
          selectedPrimaryNavGroupId={selectedPrimaryNavGroupId}
          toggleSelectedPrimaryNavGroupId={toggleSelectedPrimaryNavGroupId}
        />

        {/*
         * Inline footer elements — render at the end of the nav content,
         * "hugging" it. Only the primary CTA is sticky/anchored.
         */}
        <div className="poly-mega-menu__drawer-inline-footer">
          {navFooterItems && navFooterItems.length > 0 && (
            <NavFooter items={navFooterItems} variant="mobile" />
          )}
          {((enableLanguagePicker && parsedLanguages.length > 0 && languagePickerCurrentLanguage) ||
            (secondaryCtaText && hasCtaDestination(secondaryCtaUrl, secondaryCtaAction, secondaryCtaTargetPopupId))) && (
            <div className="poly-mega-menu__drawer-footer-actions">
              {enableLanguagePicker && parsedLanguages.length > 0 && languagePickerCurrentLanguage && (
                <button
                  type="button"
                  className="poly-mega-menu__drawer-footer-btn"
                  aria-expanded={isLanguagePickerExpanded}
                  aria-controls={langDrawerId}
                  onClick={toggleLanguagePickerDrawer}
                >
                  <CustomIcon name="language" size="1.25em" aria-hidden="true" />
                  <span>{parsedLanguages.find(l => l.code === languagePickerCurrentLanguage)?.name || languagePickerCurrentLanguage}</span>
                </button>
              )}
              {secondaryCtaText && hasCtaDestination(secondaryCtaUrl, secondaryCtaAction, secondaryCtaTargetPopupId) && (
                <MegaMenuCta
                  text={secondaryCtaText}
                  url={secondaryCtaUrl}
                  ctaId={secondaryCtaId}
                  ldAppendix={secondaryCtaLdAppendix}
                  behavior={secondaryCtaBehavior}
                  action={secondaryCtaAction}
                  popupTargetId={secondaryCtaTargetPopupId}
                  iconName={secondaryCtaIconName}
                />
              )}
            </div>
          )}
        </div>

        {/* Sticky footer — only the primary CTA anchored to bottom */}
        {primaryCtaText && hasCtaDestination(primaryCtaUrl, primaryCtaAction, primaryCtaTargetPopupId) && (
          <footer className="poly-mega-menu__drawer-footer">
            <MegaMenuCta
              text={primaryCtaText}
              url={primaryCtaUrl}
              className="poly-mega-menu__drawer-footer-btn poly-mega-menu__drawer-footer-btn--primary"
              ctaId={primaryCtaId}
              ldAppendix={primaryCtaLdAppendix}
              behavior={primaryCtaBehavior}
              action={primaryCtaAction}
              popupTargetId={primaryCtaTargetPopupId}
            />
          </footer>
        )}
      </div>
    </section>
  </div>
);
